import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "./api";
import { apifetch } from "../../services/interceptors";
import { startProCheckout } from "../../utils/billing";

// The tenant's plan, and what it switches on.
//
// The switches live in Global Admin → Scan Modules (one row per feature, on or off per
// licence) and are enforced by each backend service — a locked feature answers 403 with
// code "feature_locked". This is the display side: locked features stay visible with a
// lock and an upgrade path rather than vanishing, because a free account that simply
// never sees them has no reason to upgrade.

// Keys the app gates on, mapped to the module names Global Admin uses. The server sends
// the authoritative names (`module_names`); these are the fallback until it answers.
export const GATES = Object.freeze({
  LEAD_GENERATION: "Lead Generation",
  SNAPSHOT: "Snapshot Scanner",
  AI_REPORTS: "AI Reports",
  BUSINESS_REVIEWS: "Business Reviews",
  BYOR: "Build-Your-Own-Reports",
});

// The switches inside Lead Generation (Global Admin → Plans & Features). Nav items carry
// `gateFeature` with one of these keys. Lead Magnets also covers Saved widgets.
export const LEAD_GEN_FEATURES = Object.freeze({
  LEAD_MAGNETS: "Lead Magnets",
  PROSPECTING: "Prospecting",
  EXTERNAL_RISK_SCAN: "External Risk Scan",
  SCAN_LINKS: "Generate Scan Link",
});

// What each gate is called on screen and what it does — the copy on the lock screen and
// in the upgrade prompt.
export const GATE_COPY = Object.freeze({
  LEAD_GENERATION: { title: "Lead Generation", body: "Lead magnets for your website, prospect scans and scan links that turn a domain into a sales conversation." },
  LEAD_MAGNETS: { title: "Lead Magnets", body: "Scan widgets for your website that capture leads while nobody is at a desk, and the saved widgets you have built." },
  PROSPECTING: { title: "Prospecting", body: "Scan a prospect's domain before a call and send them the report." },
  EXTERNAL_RISK_SCAN: { title: "External Risk Scan", body: "Run a full external risk scan against any domain." },
  SCAN_LINKS: { title: "Scan Links", body: "Send a named prospect a link that runs a scan of their own domain." },
  SNAPSHOT: { title: "Pipsqueak Snapshot", body: "Send clients a one-time scanner link and get an internal view of their environment back in this organization." },
  AI_REPORTS: { title: "AI Reports", body: "Written reports drafted from scan data in your voice and brand." },
  BUSINESS_REVIEWS: { title: "Business Reviews", body: "Assessments, review schedules and technology health for every client." },
  BYOR: { title: "Report templates", body: "Build your own report templates with your branding and layout." },
  PRO: { title: "Pipsqueak Pro", body: "Everything in Pipsqueak, for your whole team." },
});

const NOT_LOADED = {
  loaded: false, license: null, isPro: false, modules: {}, features: {}, names: GATES,
  leadGenFeatures: LEAD_GEN_FEATURES,
  // The Pro trial every new workspace starts with (backend utils/proTrial.js).
  onTrial: false, trialDaysLeft: 0, trialEndsAt: null, trialEnded: false,
};

export const PlanContext = createContext({
  ...NOT_LOADED,
  can: () => true,
  locked: () => false,
  upgrade: () => {},
  refresh: () => {},
});

export const usePlan = () => useContext(PlanContext);

/** True when the error is a server-side plan lock rather than a failure. */
export const isPlanLock = (err) => err?.status === 403 && err?.data?.code === "feature_locked";

/**
 * Loads the plan for the shell. Also finishes a Stripe checkout the user has just come
 * back from (/…?billing=success&session_id=…): v1's dashboard confirmed it, and without
 * that a new Pro tenant stayed locked until Stripe's webhook happened to arrive.
 */
export function usePlanLoader() {
  const [plan, setPlan] = useState(NOT_LOADED);
  const [justUpgraded, setJustUpgraded] = useState(false);

  const load = useCallback(async (fresh = false) => {
    try {
      const data = await api.get(`s1/api/entitlements${fresh ? "?fresh=1" : ""}`);
      setPlan({
        loaded: true,
        license: data.license,
        isPro: !!data.is_pro,
        modules: data.modules || {},
        features: data.features || {},
        names: { ...GATES, ...(data.module_names || {}) },
        leadGenFeatures: { ...LEAD_GEN_FEATURES, ...(data.lead_gen_features || {}) },
        onTrial: !!data.on_trial,
        trialDaysLeft: data.trial_days_left || 0,
        trialEndsAt: data.trial_ends_at || null,
        trialEnded: !!data.trial_ended,
      });
    } catch (e) {
      // Unknown plan: show nothing as locked. The server still enforces every gate, so
      // the worst case is a click that ends in the same upgrade message from the API.
      console.error("[v2] plan load failed", e);
      setPlan((p) => ({ ...p, loaded: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      if (params.get("billing") === "success" && sessionId) {
        try {
          await apifetch("s1/api/billing/confirm-checkout", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({ sessionId }),
          });
        } catch { /* the reconcile in /entitlements?fresh=1 is the backstop */ }
        params.delete("billing");
        params.delete("session_id");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
        if (!cancelled) setJustUpgraded(true);
        await load(true);
        return;
      }
      if (!cancelled) await load(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  // A gate is a GATES key; `feature` is a switch inside it — a LEAD_GEN_FEATURES key or
  // its name. A module with no switch at all counts as on, matching the server (isAllowed).
  const can = useCallback((gate, feature) => {
    if (!plan.loaded) return true;
    if (gate === "PRO") return plan.isPro;
    const name = plan.names[gate] || gate;
    if (plan.modules[name] === false) return false;
    const featureName = feature && (plan.leadGenFeatures[feature] || feature);
    if (featureName && plan.features[name]?.[featureName] === false) return false;
    return true;
  }, [plan]);

  // Nav items and screens carry `pro: true` (Pro licence), `gate: "KEY"` (a module) and
  // optionally `gateFeature: "KEY"` (a switch inside that module).
  const locked = useCallback(
    (item) => !!item && ((item.pro && !can("PRO")) || (item.gate && !can(item.gate, item.gateFeature))),
    [can]
  );

  // Which GATE_COPY entry explains a lock: the area's own copy when only its switch is
  // off, the whole module's when the module is.
  const lockCopyKey = useCallback((lock) => {
    if (!lock) return "PRO";
    if (lock.gate && !can(lock.gate)) return lock.gate;
    if (lock.gateFeature) return lock.gateFeature;
    return lock.gate || "PRO";
  }, [can]);

  const upgrade = useCallback(() => {
    startProCheckout();
  }, []);

  return {
    ...plan,
    can,
    locked,
    lockCopyKey,
    upgrade,
    refresh: () => load(true),
    justUpgraded,
    clearJustUpgraded: () => setJustUpgraded(false),
  };
}
